import React from 'react';
import { Box } from '@mui/material';
import SiteRenderer from 'dashboard/components/siteRenderer/SiteRenderer';

/**
 * A real miniature of the page a template produces.
 *
 * The first version drew a wireframe — grey bars standing in for headings and
 * body text. It was honest about structure and useless for choosing, because
 * every template reduced to the same stack of grey bars and none of them
 * showed what the page actually says.
 *
 * So this renders the template through `SiteRenderer`, the same component
 * that renders the live site, at full page width and scaled down with a CSS
 * transform. The result is the actual page: real headlines, real colours,
 * real layout, just small. Nothing here can drift from what gets built,
 * because it is the thing that gets built.
 *
 * A transform rather than a small viewport: at 300px wide the responsive
 * grids would collapse to one column and the preview would show a phone
 * layout for a page about to be built on a desktop. Rendering at 1200 and
 * scaling keeps the proportions honest.
 */

// The width the miniature is rendered at before scaling. Wide enough that
// multi-column sections stay multi-column.
const RENDER_WIDTH = 1200;

/** Turn a template's preview payload into the shape SiteRenderer expects. */
const toPage = (preview, brandName) => ({
  _id: 'preview',
  layout: 'default',
  sectionData: (preview || []).map((s, i) => ({
    _id: `s${i}`,
    region: s.region,
    type: s.type,
    order: i,
    backgroundColor: s.background_color,
    textColor: s.text_color,
    componentData: (s.components || []).map((c, j) => ({
      _id: `c${i}-${j}`,
      type: c.type,
      order: j,
      content: {
        text: (c.text || '').replace(/\{\{\s*brand_name\s*\}\}/gi, brandName || 'Your brand'),
        level: c.level,
        url: c.url,
        variant: c.variant,
      },
    })),
  })),
});

export default function TemplatePreview({ preview, brandName, height = 150 }) {
  const sections = Array.isArray(preview) ? preview : [];

  // A blank template genuinely has nothing to draw. Saying so beats an empty
  // frame that reads as "preview failed to load".
  if (sections.length === 0) {
    return (
      <Box
        sx={{
          height: `${height}px`,
          borderRadius: '8px',
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          color: 'text.disabled',
        }}
      >
        Empty page
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: `${height}px`,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        // White in both modes on purpose: this is the visitor's site, and a
        // preview that followed the dashboard's colour mode would show the
        // owner a page no visitor will ever see.
        bgcolor: '#fff',
        // The miniature is decoration for a card that is itself the button.
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          width: RENDER_WIDTH,
          transformOrigin: 'top left',
          // Scaled by a container query rather than a fixed factor so the
          // card can be any width without the miniature over- or
          // under-filling it.
          transform: `scale(var(--tpl-scale, ${(280 / RENDER_WIDTH).toFixed(4)}))`,
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        ref={(el) => {
          if (!el?.parentElement) return;
          const fit = () => {
            const w = el.parentElement.clientWidth;
            if (w) el.style.setProperty('--tpl-scale', String(w / RENDER_WIDTH));
          };
          fit();
          // Card widths change with the dialog's own responsive grid.
          if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(fit);
            ro.observe(el.parentElement);
          }
        }}
      >
        <SiteRenderer page={toPage(sections, brandName)} brand={{ name: brandName }} />
      </Box>
    </Box>
  );
}
